import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type {
  CostCenterSummary,
  Direction,
  ProjectSummary,
  TransactionStatus,
  TransactionSummary,
} from '@bits/shared/zod';
import { formatInr, paise, parsePastedRows, rupeesToPaise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { TransactionDetailSheet } from '@/components/TransactionDetailSheet';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const ledgerTransactionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/transactions',
  component: LedgerTransactionsPage,
});

function todayIso(): string {
  const d = new Date();
  const off = (5 * 60 + 30) * 60 * 1000;
  return new Date(d.getTime() + off).toISOString().slice(0, 10);
}

type TransactionSortKey =
  | 'date'
  | 'account'
  | 'direction'
  | 'amount'
  | 'category'
  | 'description'
  | 'mapping'
  | 'status';
const EMPTY_TRANSACTIONS: TransactionSummary[] = [];

type NewTxnValues = {
  accountId: string;
  businessDate: string;
  amountRupees: number;
  direction: Direction;
  description: string;
  categoryId: string;
  mapping: 'none' | 'project' | 'costCenter';
  projectId: string;
  costCenterId: string;
};

type TransferValues = {
  fromAccountId: string;
  toAccountId: string;
  businessDate: string;
  amountRupees: number;
  description: string;
  mapping: 'none' | 'project' | 'costCenter';
  projectId: string;
  costCenterId: string;
};

function LedgerTransactionsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [accountFilter, setAccountFilter] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<TransactionStatus | ''>('');
  const [from, setFrom] = React.useState<string>('');
  const [to, setTo] = React.useState<string>('');
  const [unmappedOnly, setUnmappedOnly] = React.useState<boolean>(false);
  const [newOpen, setNewOpen] = React.useState(false);
  const [pasteOpen, setPasteOpen] = React.useState(false);
  const [transferOpen, setTransferOpen] = React.useState(false);
  const [mapTarget, setMapTarget] = React.useState<TransactionSummary | null>(null);
  const [reverseTarget, setReverseTarget] = React.useState<TransactionSummary | null>(null);
  const [detailId, setDetailId] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  });
  const categoriesQuery = useQuery({
    queryKey: ['ledger-categories'],
    queryFn: () => api.listCategories(),
  });
  const costCentersQuery = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.listCostCenters(),
  });
  const projectsQuery = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () => api.listProjects({}),
  });

  const listQuery = useQuery({
    queryKey: ['transactions', accountFilter, statusFilter, from, to, unmappedOnly],
    queryFn: () =>
      api.listTransactions({
        accountId: accountFilter || undefined,
        status: (statusFilter || undefined) as TransactionStatus | undefined,
        from: from || undefined,
        to: to || undefined,
        unmapped: unmappedOnly || undefined,
        pageSize: 100,
      }),
  });

  const accounts = accountsQuery.data ?? [];
  const categories = categoriesQuery.data ?? [];
  const costCenters = costCentersQuery.data ?? [];
  const projects = projectsQuery.data ?? [];
  const accountById = React.useMemo(
    () => new Map(accounts.map((a) => [a.id, a])),
    [accounts],
  );
  const projectById = React.useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const costCenterById = React.useMemo(() => new Map(costCenters.map((c) => [c.id, c])), [costCenters]);
  const overheadId = React.useMemo(
    () => costCenters.find((c) => c.isDefaultOverhead && c.active)?.id ?? '',
    [costCenters],
  );

  const sortAccessors = React.useMemo<SortAccessors<TransactionSummary, TransactionSortKey>>(
    () => ({
      date: (t) => t.businessDate,
      account: (t) => accountById.get(t.accountId)?.name ?? t.accountId,
      direction: (t) => t.direction,
      amount: (t) => t.amountPaise,
      category: (t) => t.categoryName,
      description: (t) => t.description,
      mapping: (t) =>
        t.projectId
          ? projectById.get(t.projectId)?.code ?? t.projectId
          : t.costCenterId
            ? costCenterById.get(t.costCenterId)?.name ?? t.costCenterId
            : 'Unmapped',
      status: (t) => t.status,
    }),
    [accountById, projectById, costCenterById],
  );
  const { rows, ...sorter } = useSortedRows(listQuery.data?.items ?? EMPTY_TRANSACTIONS, sortAccessors);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['transactions'] });
    await queryClient.invalidateQueries({ queryKey: ['unmapped'] });
    await queryClient.invalidateQueries({ queryKey: ['accounts'] });
  };

  // ----- New txn -----
  const newForm = useForm<NewTxnValues>({
    defaultValues: {
      accountId: '',
      businessDate: todayIso(),
      amountRupees: 0,
      direction: 'out',
      description: '',
      categoryId: '',
      mapping: 'none',
      projectId: '',
      costCenterId: '',
    },
  });
  const createMutation = useMutation({
    mutationFn: (v: NewTxnValues) =>
      api.createTransaction({
        accountId: v.accountId,
        businessDate: v.businessDate,
        amountPaise: Number(rupeesToPaise(v.amountRupees)),
        direction: v.direction,
        description: v.description,
        categoryId: v.categoryId || undefined,
        gstRelevant: false,
        projectId: v.mapping === 'project' ? v.projectId || undefined : undefined,
        costCenterId: v.mapping === 'costCenter' ? v.costCenterId || undefined : undefined,
      }),
    onSuccess: async () => {
      setNewOpen(false);
      newForm.reset({
        accountId: '',
        businessDate: todayIso(),
        amountRupees: 0,
        direction: 'out',
        description: '',
        categoryId: '',
        mapping: 'none',
        projectId: '',
        costCenterId: '',
      });
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  // ----- Paste rows -----
  const [pasteAccountId, setPasteAccountId] = React.useState<string>('');
  const [pasteText, setPasteText] = React.useState<string>('');
  const [pasteServerPreview, setPasteServerPreview] = React.useState<{
    okCount: number;
    errorCount: number;
  } | null>(null);
  const localParse = React.useMemo(() => parsePastedRows(pasteText), [pasteText]);
  const previewMutation = useMutation({
    mutationFn: (text: string) => api.previewPastedRows({ text }),
    onSuccess: (res) => setPasteServerPreview({ okCount: res.ok.length, errorCount: res.errors.length }),
  });
  const saveBatchMutation = useMutation({
    mutationFn: () =>
      api.createTransactionBatch({
        accountId: pasteAccountId,
        text: pasteText,
      }),
    onSuccess: async () => {
      setPasteOpen(false);
      setPasteText('');
      setPasteAccountId('');
      setPasteServerPreview(null);
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  // ----- Transfer -----
  const transferForm = useForm<TransferValues>({
    defaultValues: {
      fromAccountId: '',
      toAccountId: '',
      businessDate: todayIso(),
      amountRupees: 0,
      description: '',
      mapping: 'none',
      projectId: '',
      costCenterId: '',
    },
  });
  const transferMutation = useMutation({
    mutationFn: (v: TransferValues) =>
      api.createTransfer({
        fromAccountId: v.fromAccountId,
        toAccountId: v.toAccountId,
        businessDate: v.businessDate,
        amountPaise: Number(rupeesToPaise(v.amountRupees)),
        description: v.description,
        projectId: v.mapping === 'project' ? v.projectId || undefined : undefined,
        costCenterId: v.mapping === 'costCenter' ? v.costCenterId || undefined : undefined,
      }),
    onSuccess: async () => {
      setTransferOpen(false);
      transferForm.reset({
        fromAccountId: '',
        toAccountId: '',
        businessDate: todayIso(),
        amountRupees: 0,
        description: '',
        mapping: 'none',
        projectId: '',
        costCenterId: '',
      });
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  // ----- Map -----
  const mapForm = useForm<{ mapping: 'project' | 'costCenter'; projectId: string; costCenterId: string }>({
    defaultValues: { mapping: 'costCenter', projectId: '', costCenterId: '' },
  });
  React.useEffect(() => {
    if (mapTarget && overheadId) {
      mapForm.reset({ mapping: 'costCenter', projectId: '', costCenterId: overheadId });
    }
  }, [mapTarget, overheadId, mapForm]);
  const mapMutation = useMutation({
    mutationFn: (payload: { id: string; body: { projectId?: string; costCenterId?: string } }) =>
      api.mapTransaction(payload.id, payload.body),
    onSuccess: async () => {
      setMapTarget(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  // ----- Reverse -----
  const reverseForm = useForm<{ reason: string }>({
    defaultValues: { reason: '' },
  });
  const reverseMutation = useMutation({
    mutationFn: (payload: { id: string; reason: string }) =>
      api.reverseTransaction(payload.id, { reason: payload.reason }),
    onSuccess: async () => {
      setReverseTarget(null);
      reverseForm.reset({ reason: '' });
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Transactions</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Append-only ledger. Corrections are new reversal rows. Every row resolves to a project or cost center.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:shrink-0 lg:flex-nowrap">
          <Button onClick={() => setNewOpen(true)}>New</Button>
          <Button variant="outline" onClick={() => setPasteOpen(true)}>Paste rows</Button>
          <Button variant="outline" onClick={() => setTransferOpen(true)}>Transfer</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <div className="flex flex-col gap-1.5">
          <Label>Account</Label>
          <Select value={accountFilter} onChange={(e) => setAccountFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Status</Label>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.currentTarget.value as TransactionStatus | '')}
          >
            <option value="">All</option>
            <option value="recorded">Recorded</option>
            <option value="mapped">Mapped</option>
            <option value="reconciled">Reconciled</option>
            <option value="reversed">Reversed</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={unmappedOnly}
            onChange={(e) => setUnmappedOnly(e.currentTarget.checked)}
          />
          Unmapped only
        </label>
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
          <CardDescription>
            {listQuery.data?.total ?? 0} total
            {listQuery.data?.runningBalancePaise !== undefined
              ? ` · balance ${formatInr(paise(listQuery.data.runningBalancePaise))}`
              : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No transactions.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-[hsl(var(--muted-foreground))]">
                  <tr className="text-left">
                    <SortableTh sortKey="date" controller={sorter} className="py-2 pr-3">
                      Date
                    </SortableTh>
                    <SortableTh sortKey="account" controller={sorter} className="py-2 pr-3">
                      Account
                    </SortableTh>
                    <SortableTh sortKey="direction" controller={sorter} className="py-2 pr-3">
                      Dir
                    </SortableTh>
                    <SortableTh sortKey="amount" controller={sorter} align="right" className="py-2 pr-3">
                      Amount
                    </SortableTh>
                    <SortableTh sortKey="category" controller={sorter} className="py-2 pr-3">
                      Category
                    </SortableTh>
                    <SortableTh sortKey="description" controller={sorter} className="py-2 pr-3">
                      Description
                    </SortableTh>
                    <SortableTh sortKey="mapping" controller={sorter} className="py-2 pr-3">
                      Mapping
                    </SortableTh>
                    <SortableTh sortKey="status" controller={sorter} className="py-2 pr-3">
                      Status
                    </SortableTh>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="border-t border-[hsl(var(--border))]">
                      <td className="py-2 pr-3">{t.businessDate}</td>
                      <td className="py-2 pr-3">{accountById.get(t.accountId)?.name ?? t.accountId}</td>
                      <td className={`py-2 pr-3 font-medium ${t.direction === 'in' ? 'text-green-700' : 'text-red-700'}`}>
                        {t.direction}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(t.amountPaise))}</td>
                      <td className="py-2 pr-3">{t.categoryName ?? '—'}</td>
                      <td className="py-2 pr-3">
                        <button
                          type="button"
                          className="text-left hover:text-[hsl(var(--brand))] hover:underline"
                          onClick={() => setDetailId(t.id)}
                        >
                          {t.description}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        {mappingBadge(t, projectById, costCenterById)}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant={t.status === 'reversed' ? 'destructive' : 'muted'}>{t.status}</Badge>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          {t.status === 'recorded' && !t.reversalOfId ? (
                            <Button variant="outline" size="sm" onClick={() => setMapTarget(t)}>
                              Map
                            </Button>
                          ) : null}
                          {t.status !== 'reversed' && !t.reversalOfId ? (
                            <Button variant="outline" size="sm" onClick={() => setReverseTarget(t)}>
                              Reverse
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <TransactionDetailSheet
        transactionId={detailId}
        onClose={() => setDetailId(null)}
        accountName={(id) => accountById.get(id)?.name ?? id}
        projectLabel={(id) => {
          const p = projectById.get(id);
          return p ? `${p.code} · ${p.name}` : id;
        }}
        costCenterLabel={(id) => costCenterById.get(id)?.name ?? id}
      />

      {/* New txn dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New transaction</DialogTitle>
            <DialogDescription>Every entry is append-only.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={newForm.handleSubmit((v) => createMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="Account"
                required
                error={newForm.formState.errors.accountId?.message}
                hint={accounts.length === 0 ? 'No accounts yet — add one under Ledger → Accounts.' : undefined}
              >
                <Select {...newForm.register('accountId', { required: 'Pick an account' })}>
                  <option value="">Select…</option>
                  {accounts.filter((a) => a.active).map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Date" required error={newForm.formState.errors.businessDate?.message}>
                <Input type="date" {...newForm.register('businessDate', { required: 'Pick a date' })} />
              </Field>
              <Field label="Direction" required>
                <Select {...newForm.register('direction')}>
                  <option value="in">In (credit)</option>
                  <option value="out">Out (debit)</option>
                </Select>
              </Field>
              <Field
                label="Amount (INR)"
                required
                error={newForm.formState.errors.amountRupees?.message}
              >
                <Input
                  type="number"
                  step="0.01"
                  {...newForm.register('amountRupees', {
                    valueAsNumber: true,
                    required: 'Enter an amount',
                    min: { value: 0.01, message: 'Amount must be positive' },
                  })}
                />
              </Field>
              <div className="flex flex-col gap-1.5">
                <Label>Category</Label>
                <Select {...newForm.register('categoryId')}>
                  <option value="">— none —</option>
                  {categories.filter((c) => c.active).map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.kind})</option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Mapping</Label>
                <Select {...newForm.register('mapping')}>
                  <option value="none">Unmapped (queued)</option>
                  <option value="project">Project</option>
                  <option value="costCenter">Cost center</option>
                </Select>
              </div>
              {newForm.watch('mapping') === 'project' ? (
                <div className="col-span-2 flex flex-col gap-1.5">
                  <Label>Project</Label>
                  <Select {...newForm.register('projectId')}>
                    <option value="">Select…</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                    ))}
                  </Select>
                </div>
              ) : null}
              {newForm.watch('mapping') === 'costCenter' ? (
                <div className="col-span-2 flex flex-col gap-1.5">
                  <Label>Cost center</Label>
                  <Select {...newForm.register('costCenterId')}>
                    <option value="">Select…</option>
                    {costCenters.filter((c) => c.active).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.isDefaultOverhead ? ' (default)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
            </div>
            <Field label="Description" required error={newForm.formState.errors.description?.message}>
              <Input {...newForm.register('description', { required: 'Enter a description' })} />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Record'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Paste rows dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Paste rows from Excel/Sheets</DialogTitle>
            <DialogDescription>
              Columns: date, amount, direction, description, category (optional). Header row optional.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Account</Label>
              <Select value={pasteAccountId} onChange={(e) => setPasteAccountId(e.currentTarget.value)}>
                <option value="">Select…</option>
                {accounts.filter((a) => a.active).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Rows</Label>
              <Textarea
                rows={10}
                value={pasteText}
                onChange={(e) => setPasteText(e.currentTarget.value)}
                placeholder={'2026-02-01\t1500.00\tout\tAWS invoice\tCloud\n2026-02-02\t20000.00\tin\tClient advance\t'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Local parse</div>
                <div>{localParse.ok.length} ok · {localParse.errors.length} errors</div>
                {localParse.errors.length > 0 ? (
                  <ul className="mt-1 max-h-40 overflow-auto text-xs text-red-700">
                    {localParse.errors.map((e, i) => (
                      <li key={i}>line {e.line}: {e.message}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Server preview</div>
                {pasteServerPreview ? (
                  <div>{pasteServerPreview.okCount} ok · {pasteServerPreview.errorCount} errors</div>
                ) : (
                  <div className="text-[hsl(var(--muted-foreground))]">Not run yet.</div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  disabled={!pasteText || previewMutation.isPending}
                  onClick={() => previewMutation.mutate(pasteText)}
                >
                  {previewMutation.isPending ? 'Previewing…' : 'Preview on server'}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button
                disabled={
                  !pasteAccountId ||
                  !pasteText ||
                  localParse.errors.length > 0 ||
                  localParse.ok.length === 0 ||
                  saveBatchMutation.isPending
                }
                onClick={() => saveBatchMutation.mutate()}
              >
                {saveBatchMutation.isPending ? 'Saving…' : `Save all (${localParse.ok.length})`}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Transfer between accounts</DialogTitle>
            <DialogDescription>
              Creates two linked rows (out from source, in to destination) that net to zero.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={transferForm.handleSubmit((v) => transferMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="From" required error={transferForm.formState.errors.fromAccountId?.message}>
                <Select {...transferForm.register('fromAccountId', { required: 'Pick the source account' })}>
                  <option value="">Select…</option>
                  {accounts.filter((a) => a.active).map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="To" required error={transferForm.formState.errors.toAccountId?.message}>
                <Select
                  {...transferForm.register('toAccountId', {
                    required: 'Pick the destination account',
                    validate: (v, all) =>
                      v !== all.fromAccountId || 'Source and destination must differ',
                  })}
                >
                  <option value="">Select…</option>
                  {accounts.filter((a) => a.active).map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Date" required error={transferForm.formState.errors.businessDate?.message}>
                <Input type="date" {...transferForm.register('businessDate', { required: 'Pick a date' })} />
              </Field>
              <Field
                label="Amount (INR)"
                required
                error={transferForm.formState.errors.amountRupees?.message}
              >
                <Input
                  type="number"
                  step="0.01"
                  {...transferForm.register('amountRupees', {
                    valueAsNumber: true,
                    required: 'Enter an amount',
                    min: { value: 0.01, message: 'Amount must be positive' },
                  })}
                />
              </Field>
              <div className="col-span-2">
                <Field
                  label="Description"
                  required
                  error={transferForm.formState.errors.description?.message}
                >
                  <Input {...transferForm.register('description', { required: 'Enter a description' })} />
                </Field>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Mapping</Label>
                <Select {...transferForm.register('mapping')}>
                  <option value="none">Unmapped</option>
                  <option value="project">Project</option>
                  <option value="costCenter">Cost center</option>
                </Select>
              </div>
              {transferForm.watch('mapping') === 'project' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>Project</Label>
                  <Select {...transferForm.register('projectId')}>
                    <option value="">Select…</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                    ))}
                  </Select>
                </div>
              ) : null}
              {transferForm.watch('mapping') === 'costCenter' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>Cost center</Label>
                  <Select {...transferForm.register('costCenterId')}>
                    <option value="">Select…</option>
                    {costCenters.filter((c) => c.active).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.isDefaultOverhead ? ' (default)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={transferMutation.isPending}>
                {transferMutation.isPending ? 'Saving…' : 'Record transfer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Map dialog */}
      <Dialog open={mapTarget !== null} onOpenChange={(open) => { if (!open) setMapTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Map transaction</DialogTitle>
            <DialogDescription>Exactly one of project or cost center.</DialogDescription>
          </DialogHeader>
          {mapTarget ? (
            <form
              onSubmit={mapForm.handleSubmit((v) => {
                const body: { projectId?: string; costCenterId?: string } =
                  v.mapping === 'project'
                    ? { projectId: v.projectId }
                    : { costCenterId: v.costCenterId };
                mapMutation.mutate({ id: mapTarget.id, body });
              })}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1">
                  <input type="radio" value="project" {...mapForm.register('mapping')} />
                  Project
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" value="costCenter" {...mapForm.register('mapping')} />
                  Cost center
                </label>
              </div>
              {mapForm.watch('mapping') === 'project' ? (
                <div className="flex flex-col gap-1.5">
                  <Label>Project</Label>
                  <Select {...mapForm.register('projectId')}>
                    <option value="">Select…</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                    ))}
                  </Select>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label>Cost center</Label>
                  <Select {...mapForm.register('costCenterId')}>
                    <option value="">Select…</option>
                    {costCenters.filter((c) => c.active).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.isDefaultOverhead ? ' (default)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={mapMutation.isPending}>
                  {mapMutation.isPending ? 'Saving…' : 'Map'}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Reverse dialog */}
      <Dialog open={reverseTarget !== null} onOpenChange={(open) => { if (!open) setReverseTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverse transaction</DialogTitle>
            <DialogDescription>
              A new opposite-direction row is created; the original is marked reversed.
            </DialogDescription>
          </DialogHeader>
          {reverseTarget ? (
            <form
              onSubmit={reverseForm.handleSubmit((v) =>
                reverseMutation.mutate({ id: reverseTarget.id, reason: v.reason }),
              )}
              className="flex flex-col gap-3"
            >
              <div className="text-sm">
                <div><strong>Description:</strong> {reverseTarget.description}</div>
                <div>
                  <strong>Amount:</strong> {formatInr(paise(reverseTarget.amountPaise))} ({reverseTarget.direction})
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Reason</Label>
                <Input {...reverseForm.register('reason', { required: true })} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={reverseMutation.isPending}>
                  {reverseMutation.isPending ? 'Reversing…' : 'Reverse'}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function mappingBadge(
  t: TransactionSummary,
  projectById: Map<string, ProjectSummary>,
  costCenterById: Map<string, CostCenterSummary>,
): React.ReactElement {
  if (t.projectId) {
    const p = projectById.get(t.projectId);
    return <Badge variant="default">{p?.code ?? t.projectId}</Badge>;
  }
  if (t.costCenterId) {
    const c = costCenterById.get(t.costCenterId);
    return <Badge variant="muted">{c?.name ?? t.costCenterId}</Badge>;
  }
  return <Badge variant="destructive">Unmapped</Badge>;
}
